/**
 * Procedurally-rendered "reveal pending" cover used as the placeholder
 * image for blind-mint PFP collections. Rendered with a single
 * <canvas>; no external assets, no extra round trip to OpenAI.
 *
 * Output is a square PNG. The visual carries enough Atelier flavour
 * that wallets and marketplaces showing the pre-reveal art look
 * intentional rather than broken.
 */
export async function buildBlindPlaceholder(
  size: { width: number; height: number },
  seed: string,
): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = size.width;
  canvas.height = size.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("buildBlindPlaceholder: 2d context unavailable");

  // Two-stop gradient — hue derived from the seed so different
  // collections get visibly different placeholders without anyone
  // having to upload custom art.
  const hue = hashHue(seed);
  const grad = ctx.createLinearGradient(0, 0, size.width, size.height);
  grad.addColorStop(0, `hsl(${hue}, 70%, 18%)`);
  grad.addColorStop(1, `hsl(${(hue + 40) % 360}, 65%, 36%)`);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size.width, size.height);

  // Faint diagonal grid for texture so the gradient doesn't look flat.
  ctx.strokeStyle = "rgba(255,255,255,0.05)";
  ctx.lineWidth = 1;
  const step = Math.max(20, Math.floor(size.width / 28));
  for (let i = -size.height; i < size.width; i += step) {
    ctx.beginPath();
    ctx.moveTo(i, 0);
    ctx.lineTo(i + size.height, size.height);
    ctx.stroke();
  }

  // Big "?" centered, with a soft glow underneath.
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const cx = size.width / 2;
  const cy = size.height / 2 - size.height * 0.04;
  ctx.fillStyle = `hsla(${(hue + 180) % 360}, 90%, 70%, 0.35)`;
  ctx.font = `bold ${Math.floor(size.height * 0.6)}px ui-sans-serif, system-ui, sans-serif`;
  ctx.fillText("?", cx, cy + 6);
  ctx.fillStyle = "rgba(255,255,255,0.94)";
  ctx.fillText("?", cx, cy);

  // Caption.
  ctx.fillStyle = "rgba(255,255,255,0.78)";
  ctx.font = `600 ${Math.floor(size.height * 0.045)}px ui-sans-serif, system-ui, sans-serif`;
  ctx.fillText("REVEAL PENDING", cx, size.height * 0.84);
  ctx.fillStyle = "rgba(255,255,255,0.5)";
  ctx.font = `500 ${Math.floor(size.height * 0.028)}px ui-sans-serif, system-ui, sans-serif`;
  ctx.fillText("blind mint — your hero arrives at reveal", cx, size.height * 0.9);

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("toBlob returned null"))),
      "image/png",
    );
  });
}

function hashHue(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return h % 360;
}
